import { LitElement } from 'lit';
import { customElement, query } from 'lit/decorators.js';

import { createIgniteIframeFromDataset, ignitePlayerOptionsFromDataset } from '@helpers/ignitePlayer.ts';
import { ManVideoTracking } from './man-video.tracking';
import { resolveComponentDataLayerId } from '@services/componentTracking';

@customElement('man-video')
export class ManVideo extends LitElement {
  @query('.video-wrapper')
  private videoWrapper!: HTMLElement;

  @query('.video-fill-wrapper')
  private videoFillWrapper!: HTMLElement;

  @query('.video-placeholder')
  private videoConsentMarkup!: HTMLElement;

  @query('.editorial-video-template')
  private videoTemplate!: HTMLElement;

  @query('[data-video-embed-id]')
  private videoElement!: HTMLElement;

  private isAtmo: boolean = false;
  private tracking: ManVideoTracking | null = null;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  firstUpdated() {
    this.initVideo();
  }

  initVideo = async () => {
    if (!this.videoTemplate) {
      return;
    }

    this.isAtmo = Boolean(this.querySelector('.man-video--atmo'));

    this.videoTemplate.replaceWith(this.isAtmo ? this.videoFillWrapper : this.videoWrapper);
    this.videoConsentMarkup.remove();

    this.initIgniteIframe();
  };

  initIgniteIframe = () => {
    if (!this.videoElement) {
      return;
    }

    const options = ignitePlayerOptionsFromDataset(this.videoElement);
    const iframe = createIgniteIframeFromDataset(this.videoElement);
    if (!iframe || !options) {
      return;
    }

    this.videoElement.replaceChildren(iframe);
    this.initTracking(iframe, options.videoId);
  };

  initTracking = (iframe: HTMLIFrameElement, videoId: string) => {
    this.tracking = new ManVideoTracking({
      iframe,
      videoId,
      nodeId: this.resolveNodeId(videoId),
      videoIsAtmo: this.isAtmo,
    });
  };

  /**
   * The ACDL node id is generated server-side and read back from the nearest seeded ancestor
   * (TRACKING_PATTERN §2.3) — pushing to it is what merges the component's seed and the playback
   * events into a single state, instead of the two disconnected nodes this used to produce.
   *
   * This is flavour B (§2.7): one node for the component, video identity carried as payload fields.
   * A dedicated video sub-node was tried and reverted — a video is not its own resource, so a
   * sub-node seeded from the same resource resolves its `parentId` to the section rather than to
   * the component, which makes the hierarchy actively misleading.
   *
   * Safe against trap #10 because none of the pushed keys are model-owned seed keys: the payload
   * uses `videoId`/`title`/`url`, not `id`/`dc:title`/`xdm:linkURL`.
   *
   * The fallback covers hosts with no seeded node of their own — `stageelement` and
   * `offersandpromotions` today, plus legacy-vue's `VideoPlayer.vue`, which is rendered client-side
   * with no AEM resource behind it at all.
   */
  private resolveNodeId(videoId: string): string {
    const seeded = this.closest('[data-cmp-data-layer]');

    return (seeded ? resolveComponentDataLayerId(seeded) : null) ?? `video-${videoId}`;
  }

  disconnectedCallback(): void {
    this.tracking?.destroy();
    this.tracking = null;
  }
}
